import React, { useCallback, useState } from "react";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSquare,
  faTable,
  faTh,
  faSearch,
  faThumbtack,
} from "@fortawesome/free-solid-svg-icons";
import ObjectItem from "./ObjectItem";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useDragAndDrop } from "./useDragAndDrop";

const icons = {
  rectangle: <FontAwesomeIcon className="mR-10" icon={faSquare} />,
  table: <FontAwesomeIcon className="mR-10" icon={faTable} />,
  "static-field": <FontAwesomeIcon className="mR-10" icon={faThumbtack} />,
  "text-search-field": <FontAwesomeIcon className="mR-10" icon={faSearch} />,
  "search-based-table": (
    <span>
      <FontAwesomeIcon className="mR-10" icon={faTable} />
      <FontAwesomeIcon className="mR-10" icon={faSearch} />
    </span>
  ),
  "grid-cell-field": <FontAwesomeIcon className="mR-10" icon={faTh} />,
};

const ObjectsList = ({
  objects,
  activeObjectId,
  deleteObject,
  setActiveObject,
  sortTemplateObjects,
}) => {
  const [deleting, setDeleting] = useState(false);

  const { sensors, handleDragEnd } = useDragAndDrop(
    objects,
    sortTemplateObjects,
    deleting
  );

  const handleDelete = useCallback(
    (id) => {
      setDeleting(true);
      if (window.confirm("Are you sure?")) {
        deleteObject(id);
      }
      setTimeout(() => setDeleting(false), 0);
    },
    [deleteObject]
  );

  const getPageIndex = (object, type) => {
    const { fieldProperties, tableProperties, pageIndex } = object;
    switch (type) {
      case "rectangle":
      case "grid-cell-field":
        return fieldProperties.pageIndex + 1;
      case "table":
        return !isNaN(object) &&
          !isNaN(tableProperties) &&
          !isNaN(tableProperties.start) &&
          !isNaN(tableProperties.start.pageIndex)
          ? tableProperties.start.pageIndex + 1
          : "ALL";
      case "static-field":
        return isNaN(pageIndex) ? "ALL" : object.pageIndex + 1;
      case "text-search-field":
        return isNaN(fieldProperties?.pageIndex)
          ? "ALL"
          : fieldProperties.pageIndex + 1;
      default:
        return null;
    }
  };

  const determineObjectType = (object) => {
    const { fieldProperties, objectType, tableType, tableProperties } = object;

    if (fieldProperties?.fieldType === "rectangle") return "rectangle";
    if (
      objectType === "table" &&
      !tableType &&
      typeof tableProperties !== "string"
    )
      return "table";
    if (objectType === "field" && fieldProperties?.fieldType === "static")
      return "static-field";
    if (
      objectType === "field" &&
      (fieldProperties?.fieldType === "macros" ||
        fieldProperties?.expression !== undefined)
    )
      return "text-search-field";
    if (
      objectType === "table" &&
      (tableType === "searchBased" || tableProperties?.start !== undefined)
    )
      return "search-based-table";
    if (objectType === "field" && fieldProperties?.fieldType === "structure") {
      return "grid-cell-field";
    }
    return null;
  };

  const renderObjectItem = useCallback(
    (object) => {
      const type = determineObjectType(object);
      if (!type) {
        console.error(
          `ERROR: unknown object type found: ${JSON.stringify(object)}`
        );
        return null;
      }

      return (
        <ObjectItem
          key={object.id}
          id={object.id}
          icon={icons[type]}
          object={object}
          pageIndex={getPageIndex(object, type)}
          elementClasses={classNames("object-list-item", {
            active:
              activeObjectId !== undefined &&
              object.id !== undefined &&
              activeObjectId === object.id,
          })}
          onDelete={handleDelete}
          setActiveObject={setActiveObject}
        />
      );
    },
    [activeObjectId, handleDelete, setActiveObject]
  );

  return (
    <div className="objects ta-left mt-2">
      <div className="d-flex justify-content-between">
        <h6>Objects:</h6>{" "}
        <small className="text-muted">click on object to edit</small>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="object-list">
          <SortableContext
            items={objects}
            strategy={verticalListSortingStrategy}
          >
            {objects?.map(renderObjectItem)}
          </SortableContext>
        </div>
      </DndContext>
    </div>
  );
};

export default ObjectsList;
